import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Field,
  FluentProvider,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  webLightTheme,
} from '@fluentui/react-components';
import {
  ASSET_LABEL_MAX_LENGTH,
  deleteWorkAssetFormRelationship,
  getApiErrorMessage,
  getWorkAssetFormRelationships,
  normalizeAssetLabel,
  saveWorkAssetFormRelationship,
  type IWorkAssetFormRelationship,
} from '@moko/utils';
import { getPublishedFormMetadatas } from '../../services/metadataService';
import ConfirmDialog from '../../common/ConfirmDialog';

interface Props {
  workFormDefinitionId?: number;
  tenantId?: number;
}
type FormOption = { id: number; name: string; published: boolean };
type Labels = Record<number, { singular: string; plural: string }>;

const WorkAssetFormsPanel = (props: Props) => (
  <WorkAssetFormsEditor
    key={`${props.tenantId}:${props.workFormDefinitionId}`}
    {...props}
  />
);

const WorkAssetFormsEditor = ({ workFormDefinitionId, tenantId }: Props) => {
  const [forms, setForms] = useState<FormOption[]>([]);
  const [relationships, setRelationships] = useState<
    IWorkAssetFormRelationship[]
  >([]);
  const [labels, setLabels] = useState<Labels>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [confirmClear, setConfirmClear] = useState(false);
  const controller = useRef(new AbortController());

  const load = useCallback(
    async (syncDraft = true) => {
      if (!workFormDefinitionId || !tenantId) return false;
      setLoading(true);
      try {
        const [published, links] = await Promise.all([
          getPublishedFormMetadatas(controller.current, 'Asset', tenantId),
          getWorkAssetFormRelationships(
            controller.current,
            workFormDefinitionId,
            tenantId
          ),
        ]);
        if (controller.current.signal.aborted) return false;
        const options = new Map<number, FormOption>();
        published.forEach((form) => {
          const id = form.metadata.formDefinitionId;
          if (typeof id === 'number')
            options.set(id, { id, name: form.name, published: true });
        });
        // Keep existing relationships visible even when their published version is removed.
        links.forEach((link) => {
          if (!options.has(link.assetFormDefinitionId))
            options.set(link.assetFormDefinitionId, {
              id: link.assetFormDefinitionId,
              name: link.assetFormLabel,
              published: link.publishedMetadataId != null,
            });
        });
        setForms(
          [...options.values()].sort((a, b) => a.name.localeCompare(b.name))
        );
        setRelationships(links);
        if (syncDraft) {
          setSelectedIds(links.map((link) => link.assetFormDefinitionId));
          setLabels(
            Object.fromEntries(
              links.map((link) => [
                link.assetFormDefinitionId,
                {
                  singular: link.singularLabel ?? '',
                  plural: link.pluralLabel ?? '',
                },
              ])
            )
          );
        }
        setError(undefined);
        return true;
      } catch (e) {
        if (!controller.current.signal.aborted)
          setError(
            getApiErrorMessage(e, 'Failed to load related Asset forms.')
          );
        return false;
      } finally {
        if (!controller.current.signal.aborted) setLoading(false);
      }
    },
    [workFormDefinitionId, tenantId]
  );

  useEffect(() => {
    controller.current = new AbortController();
    void load();
    return () => controller.current.abort();
  }, [load]);

  const save = async () => {
    if (!workFormDefinitionId || !tenantId) return;
    setSaving(true);
    setError(undefined);
    setSuccess(undefined);
    try {
      // Add before removing so a partial failure does not temporarily remove all restrictions.
      for (const id of selectedIds) {
        const existing = relationships.find(
          (link) => link.assetFormDefinitionId === id
        );
        const value = labels[id] ?? { singular: '', plural: '' };
        if (
          existing &&
          normalizeAssetLabel(value.singular) === existing.singularLabel &&
          normalizeAssetLabel(value.plural) === existing.pluralLabel
        )
          continue;
        await saveWorkAssetFormRelationship(
          controller.current,
          existing ? 'PUT' : 'POST',
          {
            workFormDefinitionId,
            tenantId,
            assetFormDefinitionId: id,
            singularLabel: value.singular,
            pluralLabel: value.plural,
          }
        );
      }
      for (const link of relationships.filter(
        (link) => !selectedIds.includes(link.assetFormDefinitionId)
      )) {
        await deleteWorkAssetFormRelationship(
          controller.current,
          workFormDefinitionId,
          link.assetFormDefinitionId,
          tenantId
        );
      }
      if (!controller.current.signal.aborted && (await load()))
        setSuccess('Related Asset forms saved.');
    } catch (e) {
      // Keep the draft, but reload persisted links so retry skips successful mutations.
      if (!controller.current.signal.aborted) {
        await load(false);
        setError(getApiErrorMessage(e, 'Failed to save related Asset forms.'));
      }
    } finally {
      if (!controller.current.signal.aborted) setSaving(false);
    }
  };

  if (!tenantId || !workFormDefinitionId)
    return (
      <MessageBar>
        <MessageBarBody>
          {!tenantId
            ? 'Select a tenant before managing related Asset forms.'
            : 'Save this Work form to create its stable form identity before configuring related Asset forms.'}
        </MessageBarBody>
      </MessageBar>
    );

  return (
    <FluentProvider theme={webLightTheme}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Text>
          Select the published Asset forms permitted for Works using this form.
          Relationships are environment-local and are not included in form
          exports or imports.
        </Text>
        <Text>
          With no Asset forms configured, all otherwise available Asset forms
          are permitted. Once configured, only the selected forms are permitted.
        </Text>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>{success}</MessageBarBody>
          </MessageBar>
        )}
        {loading ? (
          <Spinner label="Loading tenant Asset forms" />
        ) : forms.length ? (
          <div
            role="group"
            aria-label="Related Asset forms"
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {forms.map((form) => {
              const selected = selectedIds.includes(form.id);
              const linked = relationships.some(
                (link) => link.assetFormDefinitionId === form.id
              );
              const value = labels[form.id] ?? { singular: '', plural: '' };
              return (
                <div key={form.id}>
                  <Checkbox
                    label={
                      <span>
                        {form.name} — form identity {form.id}
                        {!form.published
                          ? ' (no current published version)'
                          : ''}
                      </span>
                    }
                    checked={selected}
                    disabled={saving || (!form.published && !linked)}
                    onChange={(_, data) => {
                      setSuccess(undefined);
                      setSelectedIds((ids) =>
                        data.checked
                          ? [...ids, form.id]
                          : ids.filter((id) => id !== form.id)
                      );
                    }}
                  />
                  {selected && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 12,
                        marginLeft: 28,
                        marginTop: 8,
                      }}
                    >
                      <Field label={`Singular label for ${form.name}`}>
                        <Input
                          value={value.singular}
                          maxLength={ASSET_LABEL_MAX_LENGTH}
                          disabled={saving}
                          onChange={(_, data) => {
                            setSuccess(undefined);
                            setLabels((current) => ({
                              ...current,
                              [form.id]: { ...value, singular: data.value },
                            }));
                          }}
                        />
                      </Field>
                      <Field label={`Plural label for ${form.name}`}>
                        <Input
                          value={value.plural}
                          maxLength={ASSET_LABEL_MAX_LENGTH}
                          disabled={saving}
                          onChange={(_, data) => {
                            setSuccess(undefined);
                            setLabels((current) => ({
                              ...current,
                              [form.id]: { ...value, plural: data.value },
                            }));
                          }}
                        />
                      </Field>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : !error ? (
          <Text>
            No published Asset forms are available for this tenant. Publish the
            Asset forms before linking them.
          </Text>
        ) : null}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            appearance="primary"
            disabled={loading || saving || !!error}
            onClick={() => {
              if (relationships.length && !selectedIds.length)
                setConfirmClear(true);
              else void save();
            }}
          >
            {saving ? 'Saving…' : 'Save related forms'}
          </Button>
          <Button
            appearance="secondary"
            disabled={loading || saving}
            onClick={() => {
              setSuccess(undefined);
              void load();
            }}
          >
            Refresh
          </Button>
        </div>
        <ConfirmDialog
          isModalOpen={confirmClear}
          hideModal={() => setConfirmClear(false)}
          message="Remove all relationships? This restores unrestricted selection of all otherwise available Asset forms."
          onYesClick={() => {
            setConfirmClear(false);
            void save();
          }}
        />
      </div>
    </FluentProvider>
  );
};
export default WorkAssetFormsPanel;
